import React, { useEffect, useRef } from 'react';

import { Box, Button, Typography } from '@mui/material';
import BackIcon from '@mui/icons-material/ArrowBack';
import { FormikValues, useFormik } from 'formik';

import {
  createProfile,
  getLanguages,
  doesPharmacyExist,
  getTypeOfUser,
  istermsAgreedStatus,
  pharmacyExists,
  resetPharmacyExists,
  userLocation,
  getUserProps,
  Language,
  GA_EVENTS,
  SignUpFlowScreens,
} from '@pharmaplan/common';

import CustomButton from '../../common/CustomButton';
import ProfileForm from '../../Profile/ProfileForm';
import { useAppDispatch } from '../../../hooks/useAppDispatch';
import { useAppSelector } from '../../../hooks/useAppSelector';
import { pharmacyPersist } from '../../../selectors/formPersistSelector';

import { filterLanguage } from '../../../helpers/Functions';

import { pharmacySteps } from '../helper';
import strings from '../../../localization';

import styles from './styles';
import useSelfService from '../../../hooks/useSelfService';
import useSignupErrors from '../../../hooks/useSignupFlow/useSignupErrors';
import useAnalytics from '../../../analytics/useAnalytics';

interface IPharmacySignupFlow {
  step: number;
  changeStep: (steps: object, values: FormikValues, type: string) => void;
  setStep: React.Dispatch<React.SetStateAction<number>>;
}

const PharmacySignupFlow = ({
  step,
  setStep,
  changeStep,
}: IPharmacySignupFlow) => {
  const dispatch = useAppDispatch();

  const isMounted = useRef(false);
  const { isSelfService } = useSelfService();

  const analytics = useAnalytics();
  const pharmacyPersistSelector = useAppSelector(pharmacyPersist);
  const userType = useAppSelector(getTypeOfUser);
  const isTerms = useAppSelector(istermsAgreedStatus);
  const location = useAppSelector(userLocation);
  const languages = useAppSelector(getLanguages);
  const userProps = useAppSelector(getUserProps);
  const isPharmacyExist = useAppSelector(pharmacyExists);

  const pharmacyStep = pharmacySteps()[step as keyof typeof pharmacySteps];
  const isLastStep = step === Object.keys(pharmacySteps()).length - 1;
  const validStep = step !== 0;
  const isStepZero = step === 0;
  const isStepGreater = step > 0;

  const buttonLabel = isLastStep ? strings.submit : strings.next;
  const { latitude, longitude } = location ?? {};
  const { pharmacyInfo, coordinates, primary, secondary, accounting } = pharmacyPersistSelector ?? {};
  const { type: pharmacyStepType, validator } = pharmacyStep;

  const { userType: role, language, ...rest } = userProps ?? {};

  const handleSubmit = (values: FormikValues) => {
    const allData = {
      ...rest,
      ...pharmacyInfo,
      ...coordinates,
      ...primary,
      ...secondary,
      ...accounting,
      ...values,
      latitude,
      longitude,
      role,
      languageId: language,
    };

    if (!isSelfService) {
      delete allData.createPharmaPlanProfile;
    }

    analytics?.logCustomEvent({
      event: GA_EVENTS.signupProgress,
      step_name: SignUpFlowScreens.Accounting,
      step_number: step,
    });

    dispatch(
      createProfile({
        params: allData,
        user: userType,
      }),
    );
  };

  const goToNextStep = (values: FormikValues) => {
    isStepZero
      ? dispatch(doesPharmacyExist(values.name))
      : changeStep(pharmacySteps(), values, pharmacyStepType);
  };

  const submit = (values: FormikValues) => {
    isLastStep ? handleSubmit(values) : goToNextStep(values);
  };

  const formik = useFormik({
    initialValues: {
      isSelfService,
      ...(isSelfService ? { createPharmaPlanProfile: false } : {}),
      ...pharmacyPersistSelector[pharmacyStepType],
      languageId: filterLanguage(languages, language ?? Language.fr)?.name,
      latitude: null,
      longitude: null,
    },

    onSubmit: submit,
    enableReinitialize: true,
    validationSchema: validator,
  });

  const handleBackButton = () => {
    isStepGreater && setStep((prevStep) =>
      prevStep - 1);
  };

  const {
    setFieldValue,
    values: formikValues,
    handleSubmit: formikHandleSubmit,
  } = formik ?? {};

  useSignupErrors({ formik });
  const { termsAndConditions } = formikValues ?? {};

  // Self service keeps its terms & conditions checkbox on the last signup
  // step; full service collects it on the first signup screen instead.
  const shouldShowSubmit = isSelfService && isLastStep && !termsAndConditions;

  const handleRender = () =>
    (
      <Box>
        <ProfileForm selected={pharmacyStepType} formik={formik} isSignup />
      </Box>
    );

  useEffect(() => {
    if (!isMounted.current) {
      isMounted.current = true;
    } else if (!isPharmacyExist) {
      changeStep(pharmacySteps(), formik.values, pharmacyStepType);
      dispatch(resetPharmacyExists());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isPharmacyExist]);

  useEffect(() => {
    if (isSelfService) {
      setFieldValue('termsAndConditions', isTerms);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isTerms]);

  return (
    <>
      {handleRender()}
      <Box sx={styles.subContainer} gap={4}>
        {validStep && (
          <Button sx={styles.back} onClick={handleBackButton}>
            <BackIcon />
            <Typography sx={styles.backText}>{strings.back}</Typography>
          </Button>
        )}
        <CustomButton
          label={buttonLabel}
          onClick={formikHandleSubmit}
          disabled={shouldShowSubmit}
        />
      </Box>
    </>
  );
};

export default PharmacySignupFlow;
